import { useEffect, useRef } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { differenceInYears } from 'date-fns';

import { computeBoosterDefault } from '../../../helpers/computeBoosterDefault';
import { removeCarSeatFromServices } from '../../../helpers/removeCarSeatFromServices';
import { useLiveServices } from '../../../hooks/useLiveServices';
import type { RiderFormValues } from '../../../types';

export const useBoosterSeatAutoCheck = () => {
  const { control, getValues, setValue } = useFormContext<RiderFormValues>();
  const dateOfBirth = useWatch({ control, name: 'dateOfBirth' });
  const liveServicesState = useLiveServices();
  const prevDobRef = useRef(dateOfBirth);

  useEffect(() => {
    const prevDob = prevDobRef.current;

    const isSameDate =
      (prevDob == null && dateOfBirth == null) ||
      (prevDob != null && dateOfBirth != null && new Date(prevDob).getTime() === new Date(dateOfBirth).getTime());

    if (isSameDate || !dateOfBirth) {
      prevDobRef.current = dateOfBirth;

      return;
    }

    // Don't advance the ref when services aren't ready — retry once they load.
    if (liveServicesState.status !== 'ready') {
      return;
    }

    prevDobRef.current = dateOfBirth;

    const age = differenceInYears(Date.now(), new Date(dateOfBirth));
    const isCSAvailable = liveServicesState.services.some((s) => s.code === 'CS' && s.available);
    const shouldAutoCheckBooster = computeBoosterDefault(age, isCSAvailable);

    setValue('boosterSeat', shouldAutoCheckBooster, { shouldDirty: true });

    if (shouldAutoCheckBooster) {
      removeCarSeatFromServices(getValues, setValue);
    }
  }, [dateOfBirth, liveServicesState, getValues, setValue]);
};
